import { useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLingui } from '@lingui/react';

import { AccessContext, type AccessContextValue } from '@/contexts/AccessContext';
import { accessAllows } from '@/hooks/useCanOpen';
import { isModule, type Module, type RouteAccess } from '@/lib/modules';
import type { GuideBundle, GuideFaqEntry, GuideGlossaryEntry, GuideSection, GuideTopic } from './types';

// One lazy chunk per locale, like the compiled lingui catalogs (`lib/i18n.ts`).
// The bundles are written by `pnpm guide:build` (predev, prebuild).
const BUNDLES = import.meta.glob('./generated/*.json', { import: 'default' }) as Record<
  string,
  () => Promise<GuideBundle>
>;

export const guideKeys = {
  all: ['guide'] as const,
  bundle: (locale: string) => [...guideKeys.all, 'bundle', locale] as const,
};

/** The guide of `locale`, the en guide when that locale has no bundle. */
export async function loadGuideBundle(locale: string): Promise<GuideBundle> {
  const loader = BUNDLES[`./generated/${locale}.json`] ?? BUNDLES['./generated/en.json'];
  if (!loader) throw new Error('The guide bundle is missing; run pnpm guide:build');
  return loader();
}

/** The guide bundle of the active locale, loaded once per locale. */
export function useGuideBundle() {
  const { i18n } = useLingui();
  return useQuery({
    queryKey: guideKeys.bundle(i18n.locale),
    queryFn: () => loadGuideBundle(i18n.locale),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * Who uses each mobile app, read like a route rule. The management app is for
 * admins only (its gates admit `authRole === 'admin'`), the employee app needs
 * one of its two modules, and staff never use the parent app.
 */
const APP_AREA_ACCESS: Record<string, RouteAccess> = {
  'app-parent': 'admin',
  'app-employee': ['mobile-employee', 'self-service'],
  'app-management': 'admin',
};

/**
 * May the session see `topic`? An app topic follows the users of its app
 * (`APP_AREA_ACCESS`). Any other topic's `modules` read like a route rule
 * (`ROUTE_MODULES`): module ids, or `admin` for admins only; an empty list is
 * everyone. Admins see every topic. UX only, like every link filter.
 */
export function canSeeTopic(
  topic: Pick<GuideTopic, 'modules' | 'area'>,
  access: Pick<AccessContextValue, 'isAdmin' | 'hasModule'> | null | undefined,
): boolean {
  if (!access) return true;
  const appAccess = APP_AREA_ACCESS[topic.area];
  if (appAccess) return accessAllows(appAccess, access);
  if (topic.modules.length === 0) return true;
  if (topic.modules.includes('admin')) return accessAllows('admin', access);
  const modules = topic.modules.filter((module): module is Module => isModule(module));
  return accessAllows(modules, access);
}

export interface VisibleGuide {
  /** The bundle's locale: the search drops its stop words (#2108). */
  locale: string;
  /** The bundle's synonym groups, for the search's query expansion (#2108). */
  synonyms: readonly (readonly string[])[];
  /** Sections with at least one visible topic, in guide order. */
  sections: (GuideSection & { topics: GuideTopic[] })[];
  /** Every section of the bundle, visible topics or not, in guide order (the sidebar mirror). */
  allSections: GuideSection[];
  topics: GuideTopic[];
  topicById: ReadonlyMap<string, GuideTopic>;
  /** The routes any topic of the bundle explains, visible to this session or not. */
  coveredRoutes: ReadonlySet<string>;
  glossary: GuideGlossaryEntry[];
  faq: GuideFaqEntry[];
}

/** The part of a bundle the session may see; glossary and FAQ follow their topic. */
export function visibleGuide(
  bundle: GuideBundle,
  access: Pick<AccessContextValue, 'isAdmin' | 'hasModule'> | null | undefined,
): VisibleGuide {
  const topics = bundle.topics.filter((topic) => canSeeTopic(topic, access));
  const topicById = new Map(topics.map((topic) => [topic.id, topic]));
  const sections = bundle.sections
    .map((section) => ({
      ...section,
      topics: section.topicIds.flatMap((id) => topicById.get(id) ?? []),
    }))
    .filter((section) => section.topics.length > 0);
  return {
    locale: bundle.locale,
    synonyms: bundle.synonyms ?? [],
    sections,
    allSections: bundle.sections,
    topics,
    topicById,
    coveredRoutes: new Set(bundle.topics.flatMap((topic) => topic.routes)),
    glossary: bundle.glossary.filter((entry) => topicById.has(entry.topicId)),
    faq: bundle.faq.filter((entry) => topicById.has(entry.topicId)),
  };
}

/** The guide of the active locale, filtered to what the session may see. */
export function useVisibleGuide(): { guide: VisibleGuide | undefined; isLoading: boolean; isError: boolean } {
  const access = useContext(AccessContext);
  const query = useGuideBundle();
  const guide = useMemo(() => (query.data ? visibleGuide(query.data, access) : undefined), [query.data, access]);
  return { guide, isLoading: query.isLoading, isError: query.isError };
}

/** The guide path of a topic, optionally at a heading. */
export function guideTopicPath(topicId: string, slug?: string): string {
  return slug ? `/guide/${topicId}#${slug}` : `/guide/${topicId}`;
}
